export const CCY_SYMBOL = { USD: "$", AED: "AED ", INR: "₹", GBP: "£", EUR: "€" };

// Exact amounts: currency-specific digit grouping, always 2 decimals.
// INR uses Indian lakh/crore grouping (₹4,10,11,795.00); USD/AED use western grouping ($479,428.00).
export function fmtMoney(v, ccy = "USD") {
  if (v === null || v === undefined || isNaN(v)) return "—";
  const sym = CCY_SYMBOL[ccy] || ccy + " ";
  const locale = ccy === "INR" ? "en-IN" : "en-US";
  const sign = Number(v) < 0 ? "-" : "";
  return sign + sym + Math.abs(Number(v)).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Compact monetary denomination. INR: K(1e3)/L(1e5)/Cr(1e7); USD/AED/others: K(1e3)/M(1e6)/B(1e9).
// Unit chosen from the ROUNDED coefficient so boundary rounding promotes to the next unit:
// 99,999 INR -> ₹1 L (not ₹100K), 999,999 INR -> ₹10 L (not ₹1,000K), 9,999,999 INR -> ₹1 Cr (not ₹100 L),
// 999,999 USD -> $1M (not $1,000K). Negative signs preserved; up to 2 decimals, trailing zeros removed.
const UNITS = {
  INR: [{ div: 1e7, suffix: " Cr" }, { div: 1e5, suffix: " L" }, { div: 1e3, suffix: "K" }],
  OTHER: [{ div: 1e9, suffix: "B" }, { div: 1e6, suffix: "M" }, { div: 1e3, suffix: "K" }],
};

function _pick(abs, ccy) {
  const units = ccy === "INR" ? UNITS.INR : UNITS.OTHER;
  const step = ccy === "INR" ? 100 : 1000;
  let chosen = null;
  for (const u of units) { if (abs >= u.div) { chosen = u; break; } }
  if (!chosen) return null;
  let coeff = parseFloat((abs / chosen.div).toFixed(2));
  if (coeff >= step) {
    const idx = units.indexOf(chosen);
    if (idx > 0) {
      const up = units[idx - 1];
      coeff = parseFloat((abs / up.div).toFixed(2));
      return { div: up.div, suffix: up.suffix, coeff };
    }
  }
  return { div: chosen.div, suffix: chosen.suffix, coeff };
}

export function fmtCompact(v, ccy = "USD") {
  if (v === null || v === undefined || isNaN(v)) return "—";
  const sym = CCY_SYMBOL[ccy] || ccy + " ";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs < 1e3) return fmtMoney(v, ccy);
  const u = _pick(abs, ccy);
  return `${sign}${sym}${u.coeff.toLocaleString(undefined, { maximumFractionDigits: 2 })}${u.suffix}`;
}

// One common denomination for a whole chart (axis + labels) based on the max absolute value,
// using the same promotion rules as fmtCompact.
export function axisDenom(maxAbs, ccy = "USD") {
  const u = _pick(Math.abs(maxAbs || 0), ccy);
  return u ? { div: u.div, suffix: u.suffix } : { div: 1, suffix: "" };
}

export function denomTick(v, denom) {
  return parseFloat((v / denom.div).toFixed(2)).toString();
}
